import type { CommunityEventData, EventsListBlockProps, NewsletterIssueData } from '../types';
import { theme, sectionColors } from '../theme';
import { eventDateLabel, pickIssue, safeHttpHref } from './newsletterShared';

/**
 * Upcoming community events (V3.2). Framework-agnostic, server-renderable. Data: `events`
 * (BlockData.events — the host fetches the community's next 30 days) or the issue's events
 * section (BlockData.newsletterIssues). Renders nothing when there is nothing coming up.
 */
export function EventsListBody({ events }: { events: CommunityEventData[] }) {
  return (
    <ul className="divide-y divide-black/10 rounded-2xl border border-black/10" style={{ backgroundColor: theme.surface }}>
      {events.map((e) => (
        <li key={e.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 py-4">
          <span className="w-28 shrink-0 text-sm font-semibold tabular-nums" style={{ color: theme.primary }}>{eventDateLabel(e.starts_at)}</span>
          <span className="min-w-0 flex-1">
            {/* The gateway refuses a non-http(s) event link on write, but this renders PERSISTED data:
                rows saved before that check, and AI-drafted issue JSON whose events come straight
                back from `rowToEvent`. A refused URL renders the title as plain text, never a dead
                or dangerous anchor. */}
            {safeHttpHref(e.url) ? <a href={safeHttpHref(e.url)!} target="_blank" rel="noopener" className="font-medium underline">{e.title}</a> : <span className="font-medium">{e.title}</span>}
            {e.location ? <span className="block text-sm opacity-70">{e.location}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function EventsListBlock(props: EventsListBlockProps & { events?: CommunityEventData[] | null; eventsCommunitySlug?: string | null; issues?: NewsletterIssueData[] }) {
  const { heading = 'What is happening nearby', subheading, maxItems = 6, issues = [] } = props;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  const fromIssue = pickIssue(issues, { issueId: props.issueId || null, communitySlug: props.communitySlug || null })?.sections.events ?? [];
  // `props.events` is host-injected for ONE community — the page's first newsletter block's, named by
  // `eventsCommunitySlug`. A second events-list bound to a different community used to inherit them and
  // render the wrong community's events under its own heading. So host events count only when this
  // block named no community of its own, or named the very one they were fetched for; otherwise the
  // block reads its own from the issue data, which pickIssue filters by slug/id.
  const ownSlug = (props.communitySlug || '').trim().toLowerCase();
  const hostSlug = (props.eventsCommunitySlug || '').trim().toLowerCase();
  const hostEventsApply = !ownSlug || (!!hostSlug && hostSlug === ownSlug);
  const hostEvents = hostEventsApply && props.events && props.events.length ? props.events : [];
  const all = hostEvents.length ? hostEvents : fromIssue;
  const items = all.slice(0, Math.max(1, Math.floor(Number(maxItems) || 6)));
  if (items.length === 0) return null;
  return (
    <section style={{ ...colors, fontFamily: theme.bodyFont }} className="px-6 py-12" data-block="events-list">
      <div className="mx-auto max-w-3xl">
        <h2 className="text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
        {subheading ? <p className="mt-2 opacity-80">{subheading}</p> : null}
        <div className="mt-6"><EventsListBody events={items} /></div>
      </div>
    </section>
  );
}
